import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  clearFullAuthSession,
  sessionTokenMatchesCurrentApi,
  setStoredAuthApiOriginFromApiBase,
} from '../utils/auth/authSession';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    try {
      if (!sessionTokenMatchesCurrentApi()) {
        clearFullAuthSession();
      }

      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const storedUser = localStorage.getItem(AUTH_USER_KEY);

      if (storedToken) {
        setToken(storedToken);
      }

      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.warn('Failed to restore auth state from localStorage:', error);
      clearFullAuthSession();
      setToken(null);
      setUser(null);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  const login = useCallback((nextToken, userData = null) => {
    localStorage.setItem(AUTH_TOKEN_KEY, nextToken);
    setStoredAuthApiOriginFromApiBase();

    if (userData) {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
    } else {
      localStorage.removeItem(AUTH_USER_KEY);
    }

    setToken(nextToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    clearFullAuthSession();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    isAuthenticated: Boolean(token),
    isInitializing,
  }), [user, token, login, logout, isInitializing]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
